import {
    Component,
    EventEmitter,
    Input,
    Output,
    OnInit,
    HostListener,
    SimpleChanges, ViewChild,
} from '@angular/core';
import { PageListComponent } from '@components/page-list/page-list.component';
import { SelectedItem } from '@models/selectedItem';
import { ChannelCategoryService } from '@service/channel-category.service';
import { ChannelGroupService } from '@service/channel-groups.service';
import { ChannelService } from '@service/channel.service';
import { ResponsiveService } from '@service/responsive.service';
import { Channel, ChannelFns } from 'ops/common/models/channel';
import { ChannelCategory } from 'ops/common/models/channel-category';
import { ChannelGroup } from 'ops/common/models/channel-group';
import { CUSTOM_GROUP_MAX_CHANNELS } from 'ops/common/validators/custom-group/custom-group-validator';
import { TREND_GRAPH_MAX_CHANNELS } from 'ops/common/validators/trend-graph/trend-graph-view-validator';
import { lastValueFrom } from 'rxjs';

export type SelectItemType = 'category' | 'group';

@Component({
    selector: 'app-search-channel-group',
    templateUrl: './search-channel-group.component.html',
    styleUrls: ['./search-channel-group.component.scss'],
    standalone: false
})
export class SearchChannelGroupComponent implements OnInit {
    readonly PREVIEW_LABEL = 'preview.icon.label';

    @Input() editChannels: SelectedChannel[];
    @Input() page;
    @Output() onChannelsSelected: EventEmitter<any[]> = new EventEmitter();
    @Output() onLoadingComplete: EventEmitter<void> = new EventEmitter();
    @Output() onShowingTable: EventEmitter<boolean> = new EventEmitter();
    @Output() onShowPreview: EventEmitter<void> = new EventEmitter();

    @ViewChild('pageList') pageList: PageListComponent;

    DEFUALT_PAGE_LIMIT: number = 5;
    DEFAULT_DISPLAY_COLUMNS: string[] = [
        'Ch. Group',
        'Ch. No.',
        'Ch. Name',
        'Category',
        'checkbox',
    ];

    channelsSelected = [];

    channels: Channel[] = [];

    channelGroups: ChannelGroup[] = [];
    channelCategories: ChannelCategory[] = [];

    filteredChannelList = [];
    categoryList: SelectedItem[] = [];
    filteredGroupList: SelectedItem[] = [];
    inputChannel = '';

    selectedGroups: SelectedItem[] = [];
    selectedCategories: SelectedItem[] = [];

    paginator: Boolean = false;
    numberOfPages: number = 0;
    displayedPageLimit: number = this.DEFUALT_PAGE_LIMIT;
    displayedColumns: string[] = this.DEFAULT_DISPLAY_COLUMNS;

    @Output() searchEmitter: EventEmitter<void> = new EventEmitter();
    dataSource: any[];

    constructor(
        private channelService: ChannelService,
        private channelCategoryService: ChannelCategoryService,
        private channelGroupService: ChannelGroupService,
        public responsive: ResponsiveService,
    ) {}

    @HostListener('document:keydown.enter', ['$event'])
    handleEnterKey(event: KeyboardEvent) {
        this.searchChannels();
    }

    async ngOnInit() {
        this.calculateDisplayedColumns();
        this.calculateDisplayedPageLimit();

        this.channelCategories = await this.channelCategoryService.getChannelCategories();
        this.createCategoryList();

        this.channelGroups = await lastValueFrom(this.channelGroupService.getGroups());
        this.updateFilteredGroups();

        this.channelService.getChannels().subscribe((next) => {
            this.channels = next.filter(channel => !ChannelFns.isChannelBlank(channel)) as Channel[];

            const idLookup = {};
            for (let i = 0; i < next.length; i++) {
                idLookup[next[i].fcuData.fcuChannelId] = next[i];
            }

            this.onLoadingComplete.emit();
        });

        if (this.editChannels) {
            this.editChannels.forEach((channel) => {
                this.channelsSelected.push({
                    name: channel.name,
                    channelId: channel.channelId,
                    fcuChannelId: channel.fcuChannelId,
                });
            });
            if (this.page !== PageTitle.CustomGraph) {
                this.onChannelsSelected.emit(this.channelsSelected);
            }
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.editChannels) {
            if (this.page === PageTitle.CustomGraph) {
                this.channelsSelected = [];
                this.editChannels.forEach((channel) => {
                    this.channelsSelected.push({
                        name: channel.name,
                        channelId: channel.channelId,
                        fcuChannelId: channel.fcuChannelId,
                    });
                });
                this.searchChannels(false);
            }
        }
        if(changes.editChannels && !this.editChannels?.length) {
            if (this.page === PageTitle.CustomGraph) {
                if(this.pageList) this.pageList.currentPage = 1;
            }
        }
    }

    onGropListChange(selectItems: SelectedItem[]) {
        this.selectedGroups = selectItems;
        this.searchChannels();
    }

    onCategoryListChange(selectItems: SelectedItem[]) {
        this.selectedCategories = selectItems;
        this.updateFilteredGroups();
    }

    updateFilteredGroups() {
        const selectedGroupsIds = new Set();
        this.selectedGroups.map((group) => selectedGroupsIds.add(group.id));

        if (this.selectedCategories.length === 0) {
            this.filteredGroupList = this.channelGroups.map((group) => ({
                name: group.displayName,
                selected: selectedGroupsIds.has(group.id),
                id: group.id,
            }));
        } else {
            const selectedCategoriesIds = new Set();
            this.selectedCategories.map((category) => selectedCategoriesIds.add(category.id));
            const filteredList = this.channelGroups.filter((group) =>
                selectedCategoriesIds.has(group.categoryId),
            );
            this.filteredGroupList = filteredList.map((group) => ({
                name: group.displayName,
                selected: selectedGroupsIds.has(group.id),
                id: group.id,
            }));
        }
    }

    createCategoryList() {
        this.categoryList = this.channelCategories.map((category) => ({
            id: category.id,
            name: category.category,
            selected: false,
        }));
    }

    unselectGroup(selectedGroup) {
        this.selectedGroups = this.selectedGroups.filter(
            (item) => item.name !== selectedGroup.name,
        );
        this.updateFilteredGroups();
        this.searchChannels();
    }

    unselectCategory(selectedCategory) {
        this.selectedCategories = this.selectedCategories.filter(
            (item) => item.name !== selectedCategory.name,
        );
        this.updateFilteredGroups();
        this.searchChannels();
    }

    createGroupListFromChannelGroups(channelGroups: ChannelGroup[]) {
        return channelGroups.map((channelGroup) => {
            const findGroup = this.filteredGroupList.find((group) => group.id === channelGroup.id);
            return {
                name: channelGroup.displayName,
                selected: findGroup ? findGroup.selected : false,
                id: channelGroup.id,
            };
        });
    }

    searchChannels(resetPage: boolean = true) {
        this.filteredChannelList = [];
        this.paginator = false;

        const selectedChannelsId: Set<any> = new Set();
        this.channelsSelected.forEach((channel) => selectedChannelsId.add(channel.channelId));

        const selectedGroupsId: Set<any> = new Set();
        this.selectedGroups.forEach((group) => selectedGroupsId.add(group.id));

        const selectedCategoriesId: Set<any> = new Set();
        this.selectedCategories.forEach((category) => selectedCategoriesId.add(category.id));

        this.filteredChannelList = this.channels.filter((c: Channel) => {
            let shouldShowChannel = false;
            if (
                selectedGroupsId.size === 0 &&
                selectedCategoriesId.size === 0 &&
                this.inputChannel.length === 0
            ) {
                return false;
            } else {
                if (this.inputChannel.length === 0) {
                    if (selectedGroupsId.has(c.groupId)) {
                        c.checked = selectedChannelsId.has(c.id);
                        shouldShowChannel = true;
                    }
                } else if (
                    c.opsData.name.includes(this.inputChannel) ||
                    c.fcuData.fcuChannelId.toString().includes(this.inputChannel)
                ) {
                    if (selectedGroupsId.has(c.groupId)) {
                        c.checked = selectedChannelsId.has(c.id);
                        shouldShowChannel = true;
                    } else if (selectedGroupsId.size == 0) {
                        shouldShowChannel = true;
                    }
                }
            }

            return shouldShowChannel;
        });

        this.onShowingTable.emit(this.filteredChannelList.length > 0);

        if (this.filteredChannelList.length > 9) {
            this.paginator = true;
        }
        this.numberOfPages = Math.ceil(this.filteredChannelList.length / 9);

        if(resetPage) {
            this.calculateDisplayData(0);
        }
    }

    calculateDisplayData(page) {
        this.dataSource = this.filteredChannelList.slice(9 * (page + 1) - 9, 9 * (page + 1));
    }

    checkChannel(channel, boxClicked) {
        if (this.page === PageTitle.CustomGraph) {
            this.filteredChannelList.forEach((filterChannel) => {
                if(filterChannel.id === channel.id) {
                    if(!boxClicked) {
                        if(channel.checked) {
                            channel.checked = false;
                        }else {
                            channel.checked = true;
                        }
                    }
                } else {
                    filterChannel.checked = false;
                }
            });
            this.onChannelsSelected.emit(channel);
            return;
        }

        if (channel.checked && !boxClicked) {
            this.unselectChannel(channel.id);
        } else if (!channel.checked && boxClicked) {
            this.channelsSelected = this.channelsSelected.filter(
                (channelSelected) => channelSelected.channelId !== channel.id,
            );
        } else if (
            this.page === PageTitle.TrendGraph &&
            this.channelsSelected.length >= TREND_GRAPH_MAX_CHANNELS
        ) {
            if (channel.checked) {
                channel.checked = false;
            }
            return;
        } else if (
            this.page === PageTitle.ChannelGroup &&
            this.channelsSelected.length >= CUSTOM_GROUP_MAX_CHANNELS
        ) {
            if (channel.checked) {
                channel.checked = false;
            }
            return;
        } else if (channel.checked && boxClicked) {
            this.channelsSelected.push({
                fcuChannelId: channel.fcuData.fcuChannelId,
                name: channel.opsData.name,
                channelId: channel.id,
            });
        } else if (!channel.checked && !boxClicked) {
            channel.checked = true;
            this.channelsSelected.push({
                fcuChannelId: channel.fcuData.fcuChannelId,
                name: channel.opsData.name,
                channelId: channel.id,
            });
        }

        this.onChannelsSelected.emit(this.channelsSelected);
    }

    unselectChannel(channelId) {
        if (this.page === PageTitle.CustomGraph) {
            return;
        }
        this.channelsSelected = this.channelsSelected.filter(
            (channelSelected) => channelSelected.channelId !== channelId,
        );
        if (this.dataSource) {
            this.dataSource.forEach((channel) => {
                if (channel.id === channelId) {
                    channel.checked = false;
                }
            });
        }
        this.onChannelsSelected.emit(this.channelsSelected);
    }

    checkGroupName(groupId) {
        let groupName;
        this.channelGroups.forEach((group) => {
            if (group.id === groupId) {
                groupName = group.displayName;
            }
        });
        return groupName;
    }

    searchInputChannel(value) {
        this.inputChannel = value;
        this.searchChannels();
    }

    @HostListener('window:resize')
    handleWindowResize() {
        this.calculateDisplayedColumns();
        this.calculateDisplayedPageLimit();
    }

    calculateDisplayedColumns() {
        let displayedColumnsCopy = this.displayedColumns;
        for (let i = 0; i < this.DEFAULT_DISPLAY_COLUMNS.length; i++) {
            if (this.responsive.isXSmall() || this.responsive.isHandsetPortrait()) {
                const firstPartList = this.DEFAULT_DISPLAY_COLUMNS.slice(1, 3);
                const secondPartList = this.DEFAULT_DISPLAY_COLUMNS.slice(-1);
                this.displayedColumns = [...firstPartList, ...secondPartList];
            } else if (!this.responsive.isXLarge()) {
                this.displayedColumns = this.DEFAULT_DISPLAY_COLUMNS.slice(1);
            } else {
                this.displayedColumns = this.DEFAULT_DISPLAY_COLUMNS;
            }
        }
        for (let i = 0; i < this.displayedColumns.length; i++) {
            if (this.displayedColumns[i] !== displayedColumnsCopy[i]) {
                this.searchChannels();
            }
        }
    }

    calculateDisplayedPageLimit() {
        if (this.responsive.isXSmall() || this.responsive.isHandsetPortrait()) {
            this.displayedPageLimit = 1;
        } else {
            this.displayedPageLimit = this.DEFUALT_PAGE_LIMIT;
        }
    }

    shouldDisplayGraphPreviewIcon() {
        return (
            this.channelsSelected.length > 0 &&
            !this.responsive.isXLarge() &&
            !this.responsive.isLarge()
        );
    }

    getPreviewIconLabel() {
        if (this.page === PageTitle.TrendGraph) {
            return;
        }
        return (
            this.page === PageTitle.TrendGraph &&
            this.channelsSelected.length > 0 &&
            !this.responsive.isXLarge() &&
            !this.responsive.isLarge()
        );
    }

    onShowPreviewEmit() {
        this.onShowPreview.emit();
    }

    public get previewLabel() {
        return this.PREVIEW_LABEL;
    }
}

export interface SelectedChannel {
    name: string;
    channelId: string;
    fcuChannelId: number;
    color?: string;
}

export enum PageTitle {
    TrendGraph = 'trendGraph',
    ChannelGroup = 'channelGroup',
    CustomGraph = 'customGraph',
}
